/**
 * ERP 입금 원장 클라이언트 (E5). erp.js 가 import 한다.
 *   setupERPPayments(card, {docId, seed, L, money, num, canPay, today, post, onChange}) → api
 *     card = #erpPayments (청구서 편집기 합계 아래). 행(입금일 · 금액 · 방법 · 메모 · 기록자 · ✕) +
 *     항상 존재하는 퀵 행(Enter 저장 · Esc 초기화 · 전액 입금). ✕ → 실행 취소 토스트(복원).
 *   buildQuickPayRow({docId, balance, L, money, num, today, post, onSaved, onCancel}) → element
 *     목록 행 아래 인라인 폼(우클릭 '입금 기록…')에서도 같은 퀵 행을 쓴다.
 * client_key: 퀵 행이 만들어질 때 UUID 발급, 저장 성공 후 새 키 — 같은 키 재전송은 서버가 기존 행을 돌려준다
 * (더블 서브밋 방어). 전송 중 Enter 는 무시.
 */
import { showToast, history, performUndo, refreshOrReload } from './calendar-history.js';
import { attachDatePicker, syncDateLabel } from './datepicker.js';
import { initInappSelects, syncSelect } from './inapp-select.js';
import { personEl } from './people-picker.js';

export const PAY_METHODS = ['transfer', 'card', 'cash', 'offset', 'other'];

function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); });
}
function methodLabel(L, m) { return (L.methods && L.methods[m]) || m; }

export function buildQuickPayRow(opts) {
    const o = opts || {};
    const L = o.L || {};
    const money = o.money || ((n) => (Number(n) || 0).toLocaleString('ko-KR'));
    const num = o.num || ((v) => Number(String(v).replace(/[^0-9.-]/g, '')) || 0);
    let clientKey = uuid();
    let sending = false;
    let savedAt = 0, touched = false; // Enter 연타: 저장 직후 입력을 바꾸지 않은 재전송은 무시
    const row = document.createElement('div');
    row.className = 'erp-pay-quick';
    row.innerHTML = '<span class="erp-pay-c-date"><div class="att-dp"><button type="button" class="form-input att-dp-btn erp-pay-date-btn"><span class="att-dp-label"></span></button><input type="hidden" class="erp-pay-date"></div></span>'
        + '<span class="erp-pay-c-amount"><input class="form-input num erp-pay-amount" inputmode="numeric" autocomplete="off"></span>'
        + '<span class="erp-pay-c-method"><select class="form-input erp-pay-method" data-inapp-select></select></span>'
        + '<span class="erp-pay-c-memo"><input class="form-input erp-pay-memo" maxlength="500" autocomplete="off"></span>'
        + '<span class="erp-pay-c-act"><button type="button" class="btn btn-primary btn-sm erp-pay-save"></button><button type="button" class="btn btn-ghost btn-sm erp-pay-cancel" hidden>✕</button></span>';
    const dateBtn = row.querySelector('.erp-pay-date-btn'), dateHidden = row.querySelector('.erp-pay-date');
    const amount = row.querySelector('.erp-pay-amount'), method = row.querySelector('.erp-pay-method'), memo = row.querySelector('.erp-pay-memo');
    const saveBtn = row.querySelector('.erp-pay-save'), cancelBtn = row.querySelector('.erp-pay-cancel');
    PAY_METHODS.forEach((m) => { const op = document.createElement('option'); op.value = m; op.textContent = methodLabel(L, m); method.appendChild(op); });
    amount.placeholder = L.paymentAmount || '';
    memo.placeholder = L.memo || '';
    saveBtn.textContent = L.recordPayment || '＋';
    if (o.onCancel) { cancelBtn.hidden = false; cancelBtn.addEventListener('click', () => o.onCancel()); }
    function reset() {
        dateHidden.value = o.today || new Date().toISOString().slice(0, 10);
        syncDateLabel(dateBtn, dateHidden, L.pickDate);
        amount.value = o.balance() > 0 ? money(o.balance()) : '';
        method.value = 'transfer'; syncSelect(method);
        memo.value = '';
        row.classList.remove('err');
    }
    syncDateLabel(dateBtn, dateHidden, L.pickDate);
    attachDatePicker(dateBtn, dateHidden, { placeholder: L.pickDate });
    initInappSelects(row);
    reset();
    amount.addEventListener('blur', () => { const n = Math.round(num(amount.value)); amount.value = n > 0 ? money(n) : ''; });
    amount.addEventListener('input', () => { row.classList.remove('err'); touched = true; });
    memo.addEventListener('input', () => { touched = true; });
    method.addEventListener('change', () => { touched = true; });
    dateHidden.addEventListener('change', () => { touched = true; });
    function submit(full) {
        if (sending) return Promise.resolve(false);
        if (!full && !touched && Date.now() - savedAt < 600) return Promise.resolve(false);
        const n = full ? o.balance() : Math.round(num(amount.value));
        if (!(n > 0)) { amount.focus(); row.classList.add('err'); return Promise.resolve(false); }
        if (n > o.balance()) { amount.focus(); row.classList.add('err'); showToast(L.overpay || '', { error: true }); return Promise.resolve(false); }
        sending = true; saveBtn.disabled = true; row.classList.add('sending');
        return o.post('/api/erp/docs/' + encodeURIComponent(o.docId) + '/payments', { paid_on: dateHidden.value, amount: String(n), method: method.value, memo: memo.value.trim(), client_key: clientKey }, L)
            .then((data) => { clientKey = uuid(); sending = false; savedAt = Date.now(); touched = false; saveBtn.disabled = false; row.classList.remove('sending'); if (o.onSaved) o.onSaved(data); reset(); return true; })
            .catch((e) => { sending = false; saveBtn.disabled = false; row.classList.remove('sending'); row.classList.add('err'); showToast((e && e.message) || L.failed || '', { error: true }); return false; });
    }
    row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.target.closest('.att-dp-btn, .cal-sel-btn')) { e.preventDefault(); submit(false); }
        else if (e.key === 'Escape') { e.preventDefault(); if (o.onCancel) o.onCancel(); else { reset(); amount.focus(); } }
    });
    saveBtn.addEventListener('click', () => submit(false));
    row._submit = submit;
    row._reset = reset;
    row._focus = () => amount.focus();
    return row;
}

export function setupERPPayments(card, opts) {
    const o = opts || {};
    const L = o.L || {};
    const money = o.money || ((n) => (Number(n) || 0).toLocaleString('ko-KR'));
    let data = o.seed || { payments: [], paid_amount: 0, balance: 0, total: 0 };
    const list = card.querySelector('#erpPayList');
    const count = card.querySelector('#erpPaymentsCount');
    const fullBtn = card.querySelector('#erpPayFull');
    const canPay = !!o.canPay;
    let quick = null;

    function rowEl(p) {
        const r = document.createElement('div');
        r.className = 'erp-pay-row'; r.dataset.id = p.id;
        const cell = (cls, text) => { const s = document.createElement('span'); s.className = cls; if (text !== undefined) s.textContent = text; r.appendChild(s); return s; };
        cell('erp-pay-c-date mono', p.paid_on);
        cell('erp-pay-c-amount rec-num', money(p.amount));
        const m = cell('erp-pay-c-method'); const pill = document.createElement('span'); pill.className = 'erp-pay-pill m-' + p.method; pill.textContent = methodLabel(L, p.method); m.appendChild(pill);
        cell('erp-pay-c-memo rec-muted', p.memo || '');
        const by = cell('erp-pay-c-by'); if (p.created_by) by.appendChild(personEl(p.created_by, 'xs')); by.title = p.created_at || '';
        const act = cell('erp-pay-c-act');
        if (canPay) { const x = document.createElement('button'); x.type = 'button'; x.className = 'crm-x erp-pay-x'; x.textContent = '✕'; x.setAttribute('aria-label', L.delete || 'delete'); x.addEventListener('click', () => remove(p)); act.appendChild(x); }
        return r;
    }
    function apply(next) {
        data = next || data;
        list.querySelectorAll('.erp-pay-row').forEach((r) => r.remove());
        const frag = document.createDocumentFragment();
        (data.payments || []).forEach((p) => frag.appendChild(rowEl(p)));
        if (quick) list.insertBefore(frag, quick); else list.appendChild(frag);
        if (count) count.textContent = String((data.payments || []).length);
        if (fullBtn) fullBtn.hidden = !(data.balance > 0);
        if (quick) { quick.hidden = !(data.balance > 0); if (!quick.hidden) quick._reset(); }
        if (o.onChange) o.onChange(data);
    }
    // History survives SPA navigation; send updates to the currently mounted card.
    card.applyPayments = apply;
    const removing = new Set();
    function remove(p) {
        if (removing.has(p.id)) return;
        removing.add(p.id);
        o.post('/api/erp/payments/' + encodeURIComponent(p.id) + '/delete', {}, L).then((next) => {
            apply(next);
            const send = (action) => o.post('/api/erp/payments/' + encodeURIComponent(p.id) + '/' + action, {}, L).then((data) => {
                const current = document.getElementById('erpPayments');
                if (document.getElementById('erpDoc')?.dataset.id === o.docId && current?.applyPayments) current.applyPayments(data);
            });
            history.push({
                do: () => send('delete'), undo: () => send('restore'),
                // The payment response updates totals/version without discarding unsaved editor fields.
                refresh: () => document.getElementById('recList') ? refreshOrReload() : Promise.resolve(),
            });
            showToast((L.paymentRemovedUndo || '') + ' ' + money(p.amount), { actionLabel: L.undo, onAction: performUndo });
        }).catch((e) => showToast((e && e.message) || L.failed || '', { error: true })).finally(() => removing.delete(p.id));
    }
    if (canPay) {
        quick = buildQuickPayRow({ docId: o.docId, balance: () => data.balance, L, money, num: o.num, today: o.today, post: o.post, onSaved: (next) => { apply(next); showToast(L.paymentSaved || '', {}); } });
        list.appendChild(quick);
        if (fullBtn) fullBtn.addEventListener('click', () => quick._submit(true));
    }
    apply(data);
    return { apply, data: () => data, focusQuick: () => { if (quick) quick._focus(); } };
}
